import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Snackbar,
  SnackbarCloseReason,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
} from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiStatistics } from '../../../api/hexa/ApiStatistics';
import { StatisticsRequestItem } from '../../../api/hexa/models/StatisticsModels';

interface CustomerStatisticsRequestsProps {
  customerId: string;
  startDate: string;
  endDate: string;
  urlNames: string[];
  methods: string[];
  statusCodes: number[];
}

const formatDetailValue = (value: unknown): string =>
  typeof value === 'object' && value !== null
    ? JSON.stringify(value) ?? ''
    : String(value);

const statusColor = (status: number | null) => {
  if (status === null) return 'default';
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  if (status >= 300) return 'info';
  return 'success';
};

/**
 * The reason of a failed authentication (details.auth_error) is shown as a
 * readable chip, every other detail as "key: value". The tooltip shows the raw JSON.
 */
const DetailsCell = ({
  details,
}: {
  details: Record<string, unknown> | null;
}) => {
  const { t } = useTranslation();
  if (!details || Object.keys(details).length === 0) return null;

  const { auth_error: authError, ...otherDetails } = details;
  const otherDetailsText = Object.entries(otherDetails)
    .map(([key, value]) => `${key}: ${formatDetailValue(value)}`)
    .join(' · ');

  return (
    <Tooltip
      title={
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(details, null, 2)}
        </pre>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
          paddingY: '0.25rem',
        }}
      >
        {typeof authError === 'string' && (
          <Chip
            size="small"
            color="error"
            variant="outlined"
            sx={{ alignSelf: 'flex-start' }}
            label={t(`customer_statistics.requests.auth_errors.${authError}`, {
              defaultValue: authError,
            })}
          />
        )}
        {otherDetailsText && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {otherDetailsText}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

/**
 * Single requests of the customer (newest first) with the same filters of the
 * statistics chart, so that the details of each request, such as why an
 * authentication failed, can be read.
 */
const CustomerStatisticsRequests = ({
  customerId,
  startDate,
  endDate,
  urlNames,
  methods,
  statusCodes,
}: CustomerStatisticsRequestsProps) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<StatisticsRequestItem[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [error, setError] = useState({ open: false, message: '' });

  /**
   * The filters arrive as new arrays on every render of the parent, so they
   * are compared by value to avoid refetching when nothing changed
   */
  const filtersKey = JSON.stringify({
    startDate,
    endDate,
    urlNames,
    methods,
    statusCodes,
  }) ?? '';
  const filters = useMemo(
    () =>
      JSON.parse(filtersKey) as Omit<
        CustomerStatisticsRequestsProps,
        'customerId'
      >,
    [filtersKey]
  );
  const lastFiltersKey = useRef(filtersKey);
  const latestRequest = useRef(0);

  const columns = useMemo<GridColDef<StatisticsRequestItem>[]>(
    () => [
      {
        field: 'created',
        headerName: t('customer_statistics.requests.columns.created'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleString('en-GB') : '',
      },
      {
        field: 'url_name',
        headerName: t('customer_statistics.requests.columns.request'),
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
      {
        field: 'http_method',
        headerName: t('customer_statistics.requests.columns.method'),
        width: 90,
        sortable: false,
      },
      {
        field: 'status',
        headerName: t('customer_statistics.requests.columns.status'),
        width: 90,
        sortable: false,
        renderCell: (params) =>
          params.row.status === null ? null : (
            <Chip
              size="small"
              variant="outlined"
              color={statusColor(params.row.status)}
              label={params.row.status}
            />
          ),
      },
      {
        field: 'consumer',
        headerName: t('customer_statistics.requests.columns.consumer'),
        flex: 1.5,
        minWidth: 220,
        sortable: false,
        renderCell: (params) =>
          params.row.consumer ? (
            <Box sx={{ paddingY: '0.25rem' }}>
              <Typography variant="body2">
                {params.row.consumer.application}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ wordBreak: 'break-all' }}
              >
                {params.row.consumer.key}
              </Typography>
            </Box>
          ) : null,
      },
      {
        field: 'ip_address',
        headerName: t('customer_statistics.requests.columns.ip_address'),
        flex: 0.8,
        minWidth: 120,
        sortable: false,
      },
      {
        field: 'details',
        headerName: t('customer_statistics.requests.columns.details'),
        flex: 2,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => <DetailsCell details={params.row.details} />,
      },
    ],
    [t]
  );

  const fetchRequests = useCallback(async () => {
    // New filters: go back to the first page instead of fetching a page that may not exist
    if (lastFiltersKey.current !== filtersKey) {
      lastFiltersKey.current = filtersKey;
      if (paginationModel.page !== 0) {
        setPaginationModel((model) => ({ ...model, page: 0 }));
        return;
      }
    }

    // Ignore the answer of a request if a newer one has been made in the meantime
    const requestNumber = ++latestRequest.current;
    setLoading(true);
    try {
      const { total, requests } = await ApiStatistics.requests(customerId, {
        page: paginationModel.page,
        size: paginationModel.pageSize,
        start_date: filters.startDate,
        end_date: filters.endDate,
        url_names: filters.urlNames,
        methods: filters.methods,
        status_codes: filters.statusCodes,
      });
      if (requestNumber !== latestRequest.current) return;
      setRowCount(total);
      setRows(requests);
    } catch (error: any) {
      if (requestNumber !== latestRequest.current) return;
      setError({
        open: true,
        message: `Error ${error.status ?? 500}: ${
          error.response?.data.error ?? t('customer_statistics.requests.error')
        }`,
      });
    } finally {
      if (requestNumber === latestRequest.current) setLoading(false);
    }
  }, [
    customerId,
    filters,
    filtersKey,
    paginationModel.page,
    paginationModel.pageSize,
    t,
  ]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError((current) => ({ ...current, open: false }));
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: '0.5rem !important',
          paddingX: '1rem',
          marginTop: '1.5rem',
        }}
      >
        <CardHeader
          title={
            <Typography variant="h5">
              {t('customer_statistics.requests.title')}
            </Typography>
          }
        />
        <CardContent sx={{ paddingTop: '0 !important' }}>
          <DataGridPremium
            getRowId={(row) => row.id}
            loading={loading}
            rows={rows}
            columns={columns}
            paginationMode="server"
            pagination={true}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[10, 25, 50, 100]}
            autoHeight={true}
            getRowHeight={() => 'auto'}
            density={'compact'}
            disableColumnMenu
            disableRowSelectionOnClick
            localeText={{ noRowsLabel: t('customer_statistics.no_data') }}
          />
        </CardContent>
      </Card>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomerStatisticsRequests;
